import React, { useState } from 'react';
import { Select, Tooltip } from '@myua/kit';
import {
  AiProviderId,
  ASPECTS,
  Aspect,
  MagicTemplate,
  modelResolutions,
  PROMPT_MODIFIERS,
  Resolution,
} from '../model';
import type { LocalOpKind } from '../localOps/types';
import { TM } from '../tokens';
import { PencilIcon, PlusIcon } from '../icons';
import { Field, Section } from './ui';
import { LocalToolsSection } from './LocalToolsSection';

// One template in the list. Kept to a single line — its description lives in the
// hover tooltip rather than under the label, so many more templates fit in the
// panel without scrolling. Click applies the template, double-click (like the
// pencil) opens the editor.
const TemplateRow: React.FC<{
  tpl: MagicTemplate;
  active: boolean;
  onPick: () => void;
  onEdit: () => void;
}> = ({ tpl, active, onPick, onEdit }) => {
  const [hover, setHover] = useState(false);
  const postSteps = [
    tpl.postUpscale ? 'Upscale 2×' : null,
    tpl.postRemoveBg ? 'Remove background' : null,
  ].filter((s): s is string => !!s);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: 'relative' }}
    >
      {/* Tooltip wraps only the button, so hovering the pencil dismisses it
          instead of covering the row it belongs to. */}
      <Tooltip
        placement="left"
        mouseEnterDelay={0.25}
        title={
          <div style={{ fontSize: 12, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700 }}>{tpl.label}</div>
            {!!tpl.hint.trim() && <div style={{ marginTop: 2 }}>{tpl.hint}</div>}
            {postSteps.length > 0 && (
              <div style={{ marginTop: 4, opacity: 0.85 }}>
                After generation: {postSteps.join(' → ')}
              </div>
            )}
            <div style={{ marginTop: 4, opacity: 0.7 }}>Double-click to edit</div>
          </div>
        }
      >
        <button
          type="button"
          onClick={onPick}
          onDoubleClick={onEdit}
          style={{
            width: '100%',
            padding: '6px 32px 6px 10px',
            borderRadius: 6,
            textAlign: 'left',
            border: `1px solid ${active ? TM.blue : TM.border}`,
            background: active ? TM.blueWash : '#fff',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: TM.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {tpl.label}
            {tpl.custom && (
              <span
                style={{
                  fontSize: 9,
                  marginLeft: 6,
                  padding: '1px 4px',
                  borderRadius: 3,
                  background: TM.blueFaint,
                  color: TM.blue,
                  fontWeight: 700,
                }}
              >
                CUSTOM
              </span>
            )}
            {postSteps.length > 0 && (
              <span
                style={{
                  fontSize: 9,
                  marginLeft: 6,
                  padding: '1px 4px',
                  borderRadius: 3,
                  background: TM.greenSoft,
                  color: '#0f5a1f',
                  fontWeight: 700,
                }}
              >
                {[tpl.postUpscale ? '↑2×' : null, tpl.postRemoveBg ? 'NO BG' : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </div>
        </button>
      </Tooltip>
      {(hover || active) && (
        <button
          type="button"
          onClick={onEdit}
          title="Edit template"
          style={{
            position: 'absolute',
            // Vertically centred — the row is a single line now, so a fixed
            // top offset would hang below it.
            top: '50%',
            transform: 'translateY(-50%)',
            right: 5,
            width: 20,
            height: 20,
            borderRadius: 4,
            border: `1px solid ${TM.border}`,
            background: '#fff',
            cursor: 'pointer',
            color: TM.textMute,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PencilIcon size={12} />
        </button>
      )}
    </div>
  );
};

// Right panel: clickable prompt-preset templates + output settings
// (aspect ratio, prompt modifiers).
export const TemplatesPanel: React.FC<{
  templates: MagicTemplate[];
  activeTpl: string;
  onPickTemplate: (tpl: MagicTemplate) => void;
  onStartEdit: (tpl: MagicTemplate) => void;
  onAddTemplate: () => void;
  onAddModifier: (text: string) => void;
  aspect: Aspect;
  setAspect: (a: Aspect) => void;
  modelId: string;
  // Provider of the selected key (backend path) — decides whether a model the
  // catalog doesn't know still offers a resolution choice. Undefined on the
  // direct path, where the model is a legacy id with its own list.
  provider?: AiProviderId;
  resolution: Resolution;
  setResolution: (r: Resolution) => void;
  onRunLocalOp: (kind: LocalOpKind) => void;
  canRunLocalOp: boolean;
  localOpBusy: boolean;
  localOpRunningKind: LocalOpKind | null;
}> = ({
  templates,
  activeTpl,
  onPickTemplate,
  onStartEdit,
  onAddTemplate,
  onAddModifier,
  aspect,
  setAspect,
  modelId,
  provider,
  resolution,
  setResolution,
  onRunLocalOp,
  canRunLocalOp,
  localOpBusy,
  localOpRunningKind,
}) => {
  const resolutions = modelResolutions(modelId, provider);
  return (
    <div
      style={{
        width: 320,
        borderLeft: `1px solid ${TM.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        minHeight: 0,
        background: '#fff',
      }}
    >
      {/* Scrollable content — templates + output settings. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <Section
          title="Templates"
          hint="Pick a mode — its instructions apply as a system prompt on top of what you type. Click to apply, hover for the description, double-click (or the pencil) to edit. Your templates and edits are kept on this device."
          right={
            <button
              type="button"
              onClick={onAddTemplate}
              style={{
                height: 22,
                padding: '0 6px',
                borderRadius: 4,
                border: `1px solid ${TM.border}`,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 11,
                color: TM.textMute,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <PlusIcon size={10} /> New
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {templates.map((t) => (
              <TemplateRow
                key={t.id}
                tpl={t}
                active={t.id === activeTpl}
                onPick={() => onPickTemplate(t)}
                onEdit={() => onStartEdit(t)}
              />
            ))}
          </div>
        </Section>

        <Section title="Output">
          {/* Aspect ratio + target resolution, side by side as two dropdowns. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="Aspect ratio" style={{ flex: 1, marginBottom: 10 }}>
              <Select
                size="small"
                value={aspect}
                onChange={(v) => setAspect(v as Aspect)}
                style={{ width: '100%' }}
                options={ASPECTS.map((a) => ({ value: a, label: a }))}
              />
            </Field>
            {resolutions.length > 0 && (
              <Field label="Resolution" style={{ flex: 1, marginBottom: 10 }}>
                <Select
                  size="small"
                  value={resolution}
                  onChange={(v) => setResolution(v as Resolution)}
                  style={{ width: '100%' }}
                  options={resolutions.map((r) => ({ value: r, label: r }))}
                />
              </Field>
            )}
          </div>

          <Field label="Modifiers">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PROMPT_MODIFIERS.map((mod) => (
                <Tooltip key={mod.label} title={mod.description} placement="top">
                  <button
                    type="button"
                    onClick={() => onAddModifier(mod.label)}
                    style={{
                      height: 22,
                      padding: '0 8px',
                      borderRadius: 11,
                      border: `1px solid ${TM.border}`,
                      background: TM.bgSoft,
                      color: TM.textMute,
                      cursor: 'pointer',
                      fontSize: 11,
                    }}
                  >
                    + {mod.label}
                  </button>
                </Tooltip>
              ))}
            </div>
          </Field>
        </Section>
      </div>

      {/* On-device tools pinned to the bottom — they don't scroll with the
          templates/output content above. */}
      <div style={{ flexShrink: 0, padding: '0 14px 14px' }}>
        <LocalToolsSection
          onRun={onRunLocalOp}
          canRun={canRunLocalOp}
          busy={localOpBusy}
          runningKind={localOpRunningKind}
        />
      </div>
    </div>
  );
};
